import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Dock from './Dock';
import SessionExpiryDialog from './SessionExpiryDialog';
import CompleteProfileDialog from './CompleteProfileDialog';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import Breadcrumb from './Breadcrumb';
import { TopbarSlotProvider } from './TopbarSlot';
import { ShellAsideProvider, useShellAsideNode } from './ShellAside';
import './layout.scss';

/**
 * Routes the Style Guide draws on the 76px icon rail instead of the 220px
 * sidebar. Landing on one collapses the shell; leaving it restores whatever
 * the user last chose. The toggle still works while you're on them — the rail
 * is the arrival state, not a lock.
 *
 * Affiliate is on the rail because its pages run wide, not because of the
 * shell swap Trading Assessment gets — it keeps the top bar.
 * The live app has Affiliate as a single page; the whole `/affiliate/*` subtree
 * follows it here, since the sub-pages are the same wide layout.
 *
 * Profile is here for the other reason a route ends up on the rail: it hangs
 * its own full-height section aside on the shell (see ProfileAside), and that
 * column stands in for the expanded sidebar — carrying both would cost 434px
 * of chrome before the page starts. Payment & Verification is here for the
 * same reason (see VerificationAside).
 */
/** Checkout is the one page that drops the top bar as well — 1:12064. */
const BARE_ROUTES = ['/trading-assessment'];

/**
 * Full-bleed pages that keep the sidebar but nothing else of the shell: no top
 * bar, no breadcrumb, no page padding. My Alpha is a landing page with its own
 * header, drawn edge to edge beside the sidebar as in the live dashboard.
 */
const FLUSH_ROUTES = ['/my-alpha'];

const matches = (bases, pathname) =>
  bases.some((base) => pathname === base || pathname.startsWith(`${base}/`));

const PREF_KEY = 'alpha-sidebar-collapsed';

const readPref = () => {
  try {
    return localStorage.getItem(PREF_KEY) === '1';
  } catch {
    return false;
  }
};

export default function AppLayout() {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [asideCollapsed, setAsideCollapsed] = useState(false);

  const prevRouteRef = useRef(pathname);
  useEffect(() => {
    prevRouteRef.current = pathname;
    // When navigating to a new route, make sure any section aside is open by default
    setAsideCollapsed(false);
  }, [pathname]);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PREF_KEY, next ? '1' : '0');
      } catch {
        // Private mode — the choice just doesn't outlive the session.
      }
      return next;
    });
  }, []);

  const toggleAside = useCallback(() => {
    setAsideCollapsed((prev) => !prev);
  }, []);

  return (
    <TopbarSlotProvider>
      <ShellAsideProvider
        isAsideCollapsed={asideCollapsed}
        toggleAside={toggleAside}
        sidebarCollapsed={collapsed}
        toggleSidebar={toggle}
      >
        <Shell
          collapsed={collapsed}
          onToggle={toggle}
          asideCollapsed={asideCollapsed}
          bare={matches(BARE_ROUTES, pathname)}
          flush={matches(FLUSH_ROUTES, pathname)}
        />
      </ShellAsideProvider>
    </TopbarSlotProvider>
  );
}

/**
 * Split out only so it can read the aside slot AppLayout provides — a provider
 * can't consume its own context in the same component.
 */
function Shell({ collapsed, onToggle, asideCollapsed, bare, flush }) {
  const aside = useShellAsideNode();
  const mainRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }, [pathname]);

  return (
    <div
      className={`shell${aside ? ' shell--aside' : ''}${
        !collapsed ? ' shell--sidebar-expanded' : ''
      }${asideCollapsed ? ' shell--aside-collapsed' : ''}`}
    >
      <Sidebar collapsed={collapsed} onToggle={onToggle} />
      {aside}
      <div className={`shell__main${flush ? ' shell__main--flush' : ''}`} ref={mainRef}>
        {/* The wrapper stays even where the bar itself is hidden: it holds
            the 50px row gap open, so a bare page starts at the same offset
            a page with a top bar does. A flush page has no offset to match. */}
        {!flush && <div className="shell__topbar">{!bare && <Topbar />}</div>}
        <main className="shell__outlet">
          {/* The same aside a second time. A phone has no room for a column
              beside the content, and the control belongs under the top bar
              rather than above it — which flexbox can't do from out there,
              since the bar is inside this column and the aside is not. Only
              one copy is ever displayed, so only one is in the a11y tree. */}
          {aside && <div className="shell__aside-inline">{aside}</div>}
          {/* Every page but the bare checkout, which has no shell to place. */}
          {!bare && !flush && <Breadcrumb />}
          <Outlet />
        </main>
      </div>

      {/* Fixed, so it sits outside the scrolling column — and last, so it wins
          the paint order against anything the page floats. Hidden until the
          sidebar stands down. */}
      <Dock />

      {/* Only ever opens for a session signed in without "Remember me". */}
      <SessionExpiryDialog />
      <CompleteProfileDialog />
    </div>
  );
}
